import AnalyticsStore from '@/store/modules/AnalyticsStore';
import type { AnalysisStep, AnalysisSubstep } from '@/types/analytics/AnalysisStep';
import {
  CLUSTERING_ANALYSIS,
  CONSTITUENT_RISK,
  FACTSHEET,
  FOLDER,
  PERFORMANCE_ATTRIBUTION,
  POSITION,
} from '@/types/analytics/AnalysisStep';
import {
  FACTSHEET as FACTSHEET_HSBC,
  PERFORMANCE_ATTRIBUTION as PERFORMANCE_ATTRIBUTION_HSBC,
  FACTSHEET_DOWNLOAD as FACTSHEET_DOWNLOAD_HSBC,
  PERFORMANCE_ATTRIBUTION_DOWNLOAD as PERFORMANCE_ATTRIBUTION_DOWNLOAD_HSBC,
  HOME as HOME_HSBC,
} from '@/whitelabel/hsbc/types/analytics/AnalysisStep';
import { DATA as DATA_RBC, FACTSHEET as FACTSHEET_RBC } from '@/whitelabel/rbc/types/analytics/AnalysisStep';
import {
  FACTOR_COMPARISON,
  FACTOR_DECOMPOSITION,
  FACTOR_DECOMPOSITION_ROLLING,
  FACTOR_DECOMPOSITION_TABLE,
} from '@/types/analytics/FactorDecomposition';
import { PORTFOLIO_CONSTRUCTION } from '@/types/analytics/PortfolioConstruction';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { useFeatureFlag } from './useFeatureFlag';
import type { RouteLocationRaw } from 'vue-router';
import { useRouter, useRoute } from 'vue-router';
import { useIndexUniqueIdentifier } from './useCorrectIdentifier';
import getEnv from '../utils/getEnv';
import useHackyRedirectMap from './useHackyRedirectMap';
import { RouteName } from '@/constants/RouteName';
import { RiskTabs } from '@/types/analytics/ConstituentRisk';
import { UserModule } from '@/store/modules/user';
import { useLastUsedAnalysis } from './useCachedAnalysisParameters';
import { useItemUnderAnalysisGlobalState } from './useItemUnderAnalysis';
import {
  getRootItemUnderAnalysis,
  isOfficialMasterIndexFn,
  isTaggedAsLiveEquityBasketFn,
  useGetItemUnderAnalysisPortfolioTreeId,
} from '@/utils/portfolioTree';
import { getShouldUseDraftOfRootItemOnRouteFn } from './useRouteChecks';
import { usePortfolioUnderAnalysisAttributesGlobalState } from './usePortfolioUnderAnalysisAttributesGlobalState';
import { isBenchmarkStrategyItemUnderAnalysis } from '@/utils/isBenchmarkStrategyItemUnderAnalysis';

const activeAnalysisStep = computed((): AnalysisStep => AnalyticsStore.activeAnalysisStep);

export default function (): {
  activeAnalysisStep: Ref<AnalysisStep>;
} {
  return {
    activeAnalysisStep,
  };
}

const createComposableForAnalysisStep = (step: AnalysisStep) => {
  const availableAnalysisSubsteps = step.substeps;
  const activeAnalysisSubstep = ref<AnalysisSubstep | null>(null);
  const findSubstepByRoute = (route: string): AnalysisSubstep | undefined => {
    return step.substeps.find((s): boolean => s.path === route);
  };

  return (): {
    availableAnalysisSubsteps: AnalysisSubstep[];
    activeAnalysisSubstep: Ref<AnalysisSubstep | null>;
    findSubstepByRoute: (route: string) => AnalysisSubstep | undefined;
  } => {
    return {
      availableAnalysisSubsteps,
      activeAnalysisSubstep,
      findSubstepByRoute,
    };
  };
};

export const usePortfolioConstructionAnalysisSteps = createComposableForAnalysisStep(PORTFOLIO_CONSTRUCTION);
export const useConstituentRiskAnalysisSteps = createComposableForAnalysisStep(CONSTITUENT_RISK);
export const useConstituentPositionAnalysisSteps = createComposableForAnalysisStep(POSITION);

export const useFactorDecompositionAnalysisSteps = (): {
  availableAnalysisSubsteps: Ref<AnalysisSubstep[]>;
  activeAnalysisSubstep: Ref<AnalysisSubstep | null>;
} => {
  const { shouldTreatRootItemAsPortfolio } = useItemUnderAnalysisGlobalState();
  const route = useRoute();

  /**
   * Temporary filter out Factor Comparison for non-peer group users
   * Never show Factor Comparison on a strategy page
   */
  const availableAnalysisSubsteps = computed(() => {
    return shouldTreatRootItemAsPortfolio.value === true
      ? FACTOR_DECOMPOSITION.substeps
      : FACTOR_DECOMPOSITION.substeps.filter((substep: AnalysisSubstep) => substep.path !== FACTOR_COMPARISON.path);
  });

  /**
   * Compute the active analysis substep based on the current route name.
   */
  const activeAnalysisSubstep = computed(() => {
    if (route.name === RouteName.FD_REGRESSION_TABLE) {
      return FACTOR_DECOMPOSITION_TABLE;
    }

    if (route.name === RouteName.FD_ROLLING) {
      return FACTOR_DECOMPOSITION_ROLLING;
    }

    if (route.name === RouteName.FD_FACTOR_COMPARISON) {
      return FACTOR_COMPARISON;
    }

    return null;
  });

  return {
    availableAnalysisSubsteps,
    activeAnalysisSubstep,
  };
};

export function useActiveSubstep(): {
  isOnFrontierSubstep: Ref<boolean>;
  isOnPerfAttribSubstep: Ref<boolean>;
} {
  const route = useRoute();
  const isOnFrontierSubstep = computed(() => route.name === RouteName.PC_EFFICIENT_FRONTIER);
  const isOnPerfAttribSubstep = computed(() => route.name === RouteName.PC_CONTRIBUTIONS);

  return {
    isOnFrontierSubstep,
    isOnPerfAttribSubstep,
  };
}

export function useAnalysisStepItems(
  { isAnalysisToolModal }: { isAnalysisToolModal: boolean } = { isAnalysisToolModal: false },
): {
  availableAnalysisStepItems: Ref<AnalysisStep[]>;
} {
  const {
    hasPerformanceContributionData,
    hasPositionData,
    hasRiskData,
    shouldTreatRootItemAsPortfolio,
    shouldExposePositions,
    itemUnderAnalysis,
    portfolioTreeUnderAnalysis,
  } = useItemUnderAnalysisGlobalState();
  const { isConstituentPortfolio } = usePortfolioUnderAnalysisAttributesGlobalState();

  const indexUniqueIdentifier = useIndexUniqueIdentifier();

  const doesUrlContainDemoPortfolio = computed(() => {
    if (!indexUniqueIdentifier.value) return false;

    return indexUniqueIdentifier.value.includes('demo_portfolio');
  });

  const router = useRouter();

  const {
    canSwitchPortfolios,
    hasAccessToConstituentRisk,
    hasAccessToStrategyPerformanceContribution,
    hasAccessToClusteringAnalysis,
    hasAccessToFactorDecomposition,
    shouldPreventStrategyFactsheetAccess,
    hasPositionPageAccess,
    canSeeRestrictedAnalyticsToolsBofA,
  } = useFeatureFlag();

  const { isBofAEnvironment, isHSBCEnvironment, isRBCEnvironment } = getEnv();

  const availableAnalysisStepBofA = computed(() => {
    const retval: AnalysisStep[] = [];

    // We purposefully do not check for `shouldExposePositions` on BofA because we use the 'Portfolio Construction'
    // icon as a proxy for all Analytics routes on BofA (since they do not have the ApplicationNavbar)
    retval.push(PORTFOLIO_CONSTRUCTION);

    if (itemUnderAnalysis.value) {
      retval.push(FACTSHEET);
    }

    /**
     * A live basket theoretically should always have position data, but we double check here to be safe.
     */
    const shouldShowPositionForLiveBasketPortfolios = isRootItemUnderAnalysisALiveBasket.value && hasPositionData.value;

    /**
     * Non-live baskets are treated like regular portfolios.
     * If we are treating it as a regular portfolio, we show Position if it's a constituent portfolio,
     */
    const shouldShowPositionForNonLiveBasketPortfolios =
      shouldTreatRootItemAsPortfolio.value && !isRootItemUnderAnalysisALiveBasket.value && isConstituentPortfolio.value;

    /**
     * If the root item is treated as a strategy, we only show position if there is position data
     */
    const shouldShowPositionForStrategy = shouldTreatRootItemAsPortfolio.value === false && hasPositionData.value;

    const isBenchmarkStrategy = isBenchmarkStrategyItemUnderAnalysis(itemUnderAnalysis.value);

    if (
      hasPositionPageAccess.value &&
      !isBenchmarkStrategy &&
      (shouldShowPositionForLiveBasketPortfolios ||
        shouldShowPositionForNonLiveBasketPortfolios ||
        shouldShowPositionForStrategy)
    ) {
      retval.push(POSITION);
    }

    /**
     * Unlike position, risk is not always accessible for basket type portfolios.
     * Therefore, we need to ensure that basket type portfolios only show risk if they have data
     */
    const shouldShowRiskForLiveBasketPortfolios = isRootItemUnderAnalysisALiveBasket.value && hasRiskData.value;

    /**
     * Non-live baskets are treated like regular portfolios.
     * If we are treating it as a regular portfolio, we show Risk only if it's a constituent portfolio
     */
    const shouldShowRiskForNonLiveBasketPortfolios =
      shouldTreatRootItemAsPortfolio.value && !isRootItemUnderAnalysisALiveBasket.value && isConstituentPortfolio.value;

    /**
     * If the root item is treated as a strategy, we only show risk if there is risk data
     */
    const shouldShowRiskForStrategy = shouldTreatRootItemAsPortfolio.value === false && hasRiskData.value;

    if (
      hasAccessToConstituentRisk.value &&
      canSeeRestrictedAnalyticsToolsBofA.value &&
      (shouldShowRiskForLiveBasketPortfolios || shouldShowRiskForNonLiveBasketPortfolios || shouldShowRiskForStrategy)
    ) {
      retval.push(CONSTITUENT_RISK);
    }

    if (hasAccessToStrategyPerformanceContribution.value && hasPerformanceContributionData.value) {
      retval.push(PERFORMANCE_ATTRIBUTION);
    }

    return retval;
  });

  const availableAnalysisStepHSBC = computed(() => {
    const retval: AnalysisStep[] = [HOME_HSBC, FACTSHEET_HSBC, FACTSHEET_DOWNLOAD_HSBC];

    if (hasAccessToStrategyPerformanceContribution.value && hasPerformanceContributionData.value) {
      retval.push(PERFORMANCE_ATTRIBUTION_HSBC, PERFORMANCE_ATTRIBUTION_DOWNLOAD_HSBC);
    }

    return retval;
  });

  const isTaggedAsLiveEquityBasket = computed(() => {
    const rootItem = getRootItemUnderAnalysis(itemUnderAnalysis.value, portfolioTreeUnderAnalysis.value);
    if (!rootItem) return false;

    return isTaggedAsLiveEquityBasketFn(rootItem);
  });

  const isOfficialMasterIndex = computed(() => {
    const rootItem = getRootItemUnderAnalysis(itemUnderAnalysis.value, portfolioTreeUnderAnalysis.value);
    if (!rootItem) return false;

    return isOfficialMasterIndexFn(rootItem);
  });

  /**
   * We consider the root itemUnderAnalysis to be a live basket if:
   * - it is a live master index, or
   * - it is tagged as a live equity basket
   */
  const isRootItemUnderAnalysisALiveBasket = computed(() => {
    return isOfficialMasterIndex.value || isTaggedAsLiveEquityBasket.value;
  });

  /**
   * Please note that the logic for which page should be routed to (i.e., portfolio or strategy) is done below in
   * the different route objects created in `useAnalysisStepTo`
   */
  const availableAnalysisStepCore = computed(() => {
    const retval: AnalysisStep[] = [];

    if (canSwitchPortfolios.value) {
      retval.push(FOLDER);
    }

    if (
      (isAnalysisToolModal ||
        // we must show Portfolio Construction if `shouldExposePositions` is true
        shouldExposePositions.value ||
        // or if it's a regular portfolio
        shouldTreatRootItemAsPortfolio.value) &&
      !doesUrlContainDemoPortfolio.value
    ) {
      retval.push(PORTFOLIO_CONSTRUCTION);
    }

    // Registration first — router.resolve throws for unknown named routes (e.g. Barclays/SocGen/HSBC/RBC).
    const isPCARegistered = router.hasRoute(RouteName.PRINCIPAL_COMPONENT_ANALYSIS);

    if (
      isPCARegistered &&
      hasAccessToClusteringAnalysis.value &&
      !doesUrlContainDemoPortfolio.value &&
      (isAnalysisToolModal ||
        getShouldUseDraftOfRootItemOnRouteFn({
          datum: itemUnderAnalysis.value,
          route: router.resolve({ name: RouteName.PRINCIPAL_COMPONENT_ANALYSIS }),
          portfolioTreeUnderAnalysis: portfolioTreeUnderAnalysis.value,
        }) === true)
    ) {
      retval.push(CLUSTERING_ANALYSIS);
    }

    if (!itemUnderAnalysis.value) return retval;

    if (hasAccessToFactorDecomposition.value && !doesUrlContainDemoPortfolio.value) {
      retval.push(FACTOR_DECOMPOSITION);
    }

    /**
     * A live basket theoretically should always have position data, but we double check here to be safe.
     */
    const shouldShowPositionForLiveBasketPortfolios = isRootItemUnderAnalysisALiveBasket.value && hasPositionData.value;

    /**
     * Non-live baskets are treated like regular portfolios.
     * If we are treating it as a regular portfolio, we show Position
     */
    const shouldShowPositionForNonLiveBasketPortfolios =
      shouldTreatRootItemAsPortfolio.value && !isRootItemUnderAnalysisALiveBasket.value;

    /**
     * If the root item is treated as a strategy, we only show position if there is position data
     */
    const shouldShowPositionForStrategy = shouldTreatRootItemAsPortfolio.value === false && hasPositionData.value;

    if (
      hasPositionPageAccess.value &&
      (shouldShowPositionForLiveBasketPortfolios ||
        shouldShowPositionForNonLiveBasketPortfolios ||
        shouldShowPositionForStrategy)
    ) {
      retval.push(POSITION);
    }

    /**
     * Unlike position, risk is not always accessible for basket type portfolios.
     * Therefore, we need to ensure that basket type portfolios only show risk if they have data
     */
    const shouldShowRiskForLiveBasketPortfolios = isRootItemUnderAnalysisALiveBasket.value && hasRiskData.value;

    /**
     * Non-live baskets are treated like regular portfolios.
     * If we are treating it as a regular portfolio, we show Risk
     */
    const shouldShowRiskForNonLiveBasketPortfolios =
      shouldTreatRootItemAsPortfolio.value && !isRootItemUnderAnalysisALiveBasket.value;

    /**
     * If the root item is treated as a strategy, we only show risk if there is risk data
     */
    const shouldShowRiskForStrategy = shouldTreatRootItemAsPortfolio.value === false && hasRiskData.value;

    if (
      hasAccessToConstituentRisk.value &&
      (shouldShowRiskForLiveBasketPortfolios || shouldShowRiskForNonLiveBasketPortfolios || shouldShowRiskForStrategy)
    ) {
      retval.push(CONSTITUENT_RISK);
    }

    if (
      ((shouldTreatRootItemAsPortfolio.value === false && !shouldPreventStrategyFactsheetAccess.value) ||
        (shouldTreatRootItemAsPortfolio.value && !doesUrlContainDemoPortfolio.value)) &&
      !!itemUnderAnalysis.value
    ) {
      retval.push(FACTSHEET);
    }

    if (hasAccessToStrategyPerformanceContribution.value && hasPerformanceContributionData.value) {
      retval.push(PERFORMANCE_ATTRIBUTION);
    }

    return retval;
  });

  const availableAnalysisStepRBC = computed(() => {
    const retval: AnalysisStep[] = [DATA_RBC, FACTSHEET_RBC];

    if (shouldExposePositions.value || shouldTreatRootItemAsPortfolio.value) {
      retval.push(PORTFOLIO_CONSTRUCTION);
    }

    /**
     * A live basket theoretically should always have position data, but we double check here to be safe.
     */
    const shouldShowPositionForLiveBasketPortfolios = isRootItemUnderAnalysisALiveBasket.value && hasPositionData.value;

    /**
     * Non-live baskets are treated like regular portfolios.
     * If we are treating it as a regular portfolio, we show Position if it's a constituent portfolio,
     */
    const shouldShowPositionForNonLiveBasketPortfolios =
      shouldTreatRootItemAsPortfolio.value && !isRootItemUnderAnalysisALiveBasket.value && isConstituentPortfolio.value;

    /**
     * If the root item is treated as a strategy, we only show position if there is position data
     */
    const shouldShowPositionForStrategy = shouldTreatRootItemAsPortfolio.value === false && hasPositionData.value;

    if (
      hasPositionPageAccess.value &&
      (shouldShowPositionForLiveBasketPortfolios ||
        shouldShowPositionForNonLiveBasketPortfolios ||
        shouldShowPositionForStrategy)
    ) {
      retval.push(POSITION);
    }

    return retval;
  });

  const availableAnalysisStepItems = computed((): AnalysisStep[] => {
    if (isBofAEnvironment) {
      return availableAnalysisStepBofA.value;
    }

    if (isHSBCEnvironment) {
      return availableAnalysisStepHSBC.value;
    }

    if (isRBCEnvironment) {
      return availableAnalysisStepRBC.value;
    }

    return availableAnalysisStepCore.value;
  });

  return {
    availableAnalysisStepItems,
  };
}

export function useAnalysisStepTo(): {
  /**
   * If there is no `itemUnderAnalysis`, then this returns `null`.
   *
   * If the current `indexUniqueIdentifier` exists in the `hackyRedirectMap`, then this variable will return the
   * Portfolio Factsheet route with the NON Risk portfolio slug.
   *
   * else if `shouldExposePositions` is true, then it returns the Strategy Factsheet route
   *   with the current `indexUniqueIdentifier`.
   *
   * else it `shouldTreatRootItemAsPortfolio` is true, then it returns the Portfolio Factsheet route
   *   with the current `indexUniqueIdentifier`.
   *
   * else it returns the Strategy Factsheet route with the current `indexUniqueIdentifier`.
   *
   * NOTE: if the current route does not expose `indexUniqueIdentifier`, then this variable will not work as expected.
   */
  factsheetTo: Ref<RouteLocationRaw | null>;
  portfolioConstructionTo: Ref<RouteLocationRaw | null>;
  factorDecompositionTo: Ref<RouteLocationRaw | null>;
  clusteringAnalysisTo: Ref<RouteLocationRaw | null>;
  constituentTo: Ref<RouteLocationRaw | null>;
  performanceAttributionTo: Ref<RouteLocationRaw | null>;
  positionTo: Ref<RouteLocationRaw | null>;
} {
  const { itemUnderAnalysis, portfolioTreeUnderAnalysis, shouldTreatRootItemAsPortfolio } =
    useItemUnderAnalysisGlobalState();

  const { isBofAEnvironment } = getEnv();

  const indexUniqueIdentifier = useIndexUniqueIdentifier();
  const router = useRouter();
  const { lastUsedIndexUniqueIdentifier } = useLastUsedAnalysis();

  /**
   * Use the current `indexUniqueIdentifier` if it is set
   * Otherwise, use the `lastUsedIndexUniqueIdentifier` if it is set
   * Otherwise, use the default portfolio slug
   */
  const indexUniqueIdentifierToUse = computed((): string | undefined => {
    return indexUniqueIdentifier.value ?? lastUsedIndexUniqueIdentifier.value ?? UserModule.user?.defaultSlug;
  });

  const { getRedirectIdentifier, hackyRedirectMap } = useHackyRedirectMap();
  const redirectIdentifier = computed(() => getRedirectIdentifier(indexUniqueIdentifierToUse.value));
  const { hasHackyRedirectMap } = useFeatureFlag();

  const shouldUseHackyRedirectMap = computed(
    (): boolean =>
      hasHackyRedirectMap.value && !!redirectIdentifier.value && redirectIdentifier.value in hackyRedirectMap.value,
  );

  const portfolioConstructionTo = computed((): RouteLocationRaw | null => {
    if (!indexUniqueIdentifierToUse.value) return null;

    return {
      name: RouteName.PC_PERFORMANCE,
      params: {
        indexUniqueIdentifier: indexUniqueIdentifierToUse.value,
      },
    };
  });

  /**
   * Until the portfolio tree is restructured so that a portfolio tree may have both risk data
   * and a custom weighting, we are implementing a hacky map such that if Simplex users
   * are on their risk portfolio and go to Portfolio Construction, they will see their
   * custom weighting portfolio. And vice versa, if they click on Risk.
   *
   * Excepting this unusual case, if we are on a Risk portfolio, then we allow them through.
   * Otherwise we show the Analysis Tool Modal so that they can select a Risk portfolio to analyze on the Risk page.
   */
  const constituentTo = computed((): RouteLocationRaw | null => {
    if (!itemUnderAnalysis.value) return null;

    const constituentIndexUniqueIdentifier = shouldUseHackyRedirectMap.value
      ? hackyRedirectMap.value[redirectIdentifier.value!].risk
      : indexUniqueIdentifierToUse.value;

    if (!constituentIndexUniqueIdentifier) return null;

    return {
      name: RouteName.CONSTITUENT_RISK,
      params: {
        indexUniqueIdentifier: constituentIndexUniqueIdentifier,
        substep: RiskTabs.VAR,
      },
    };
  });

  /**
   * If the slug prefix matches an item in the hackyRedirectMap, then this variable will return the
   * NON-RISK portfolio slug.
   *
   * Otherwise it returns the slug for navigation (current route, last-used, or default portfolio).
   */
  const slugToUseForFactsheet = computed((): string | undefined => {
    if (shouldUseHackyRedirectMap.value) {
      // if `shouldUseHackyRedirectMap` is true, then `redirectIdentifier` is defined
      return hackyRedirectMap.value[redirectIdentifier.value!].nonRisk;
    }

    return indexUniqueIdentifierToUse.value;
  });

  const factsheetTo = computed((): RouteLocationRaw | null => {
    if (!slugToUseForFactsheet.value) return null;

    return {
      name: RouteName.FACTSHEET,
      params: {
        indexUniqueIdentifier: slugToUseForFactsheet.value,
      },
    };
  });

  /**
   * We do not show Position if `itemUnderAnalysis` is null
   */
  const positionTo = computed((): RouteLocationRaw | null => {
    if (shouldTreatRootItemAsPortfolio.value === null) return null;

    if (!indexUniqueIdentifierToUse.value) return null;

    if (isBofAEnvironment && isBenchmarkStrategyItemUnderAnalysis(itemUnderAnalysis.value)) {
      return null;
    }

    return {
      name: RouteName.POSITION,
      params: {
        indexUniqueIdentifier: indexUniqueIdentifierToUse.value,
      },
    };
  });

  const clusteringAnalysisTo = computed((): RouteLocationRaw | null => {
    if (!indexUniqueIdentifierToUse.value) return null;

    return {
      name: RouteName.PRINCIPAL_COMPONENT_ANALYSIS,
      params: {
        indexUniqueIdentifier: indexUniqueIdentifierToUse.value,
      },
    };
  });

  const itemUnderAnalysisPortfolioTreeId = useGetItemUnderAnalysisPortfolioTreeId();

  const { isPeerGroupPortfolio } = usePortfolioUnderAnalysisAttributesGlobalState();

  /**
   * Factor Decomposition is registered on Core and SocGen. Skip resolve when unregistered
   * (e.g. BofA/Barclays) or when `itemUnderAnalysis` cannot yield a landing route.
   */
  const factorDecompositionTo = computed((): RouteLocationRaw | null => {
    if (shouldTreatRootItemAsPortfolio.value === null) return null;

    // Registration first — router.resolve throws for unknown named routes (e.g. BofA/Barclays/SocGen).
    const isFactorDecompositionRegistered = router.hasRoute(RouteName.FACTOR_DECOMPOSITION);
    if (!isFactorDecompositionRegistered) return null;

    const factorDecompositionResolvedRoute = router.resolve({ name: RouteName.FACTOR_DECOMPOSITION });

    if (
      getShouldUseDraftOfRootItemOnRouteFn({
        datum: itemUnderAnalysis.value,
        route: factorDecompositionResolvedRoute,
        portfolioTreeUnderAnalysis: portfolioTreeUnderAnalysis.value,
      }) === false
    ) {
      if (!indexUniqueIdentifierToUse.value) return null;

      return {
        name: RouteName.FD_REGRESSION_TABLE,
        params: { indexUniqueIdentifier: indexUniqueIdentifierToUse.value },
      };
    }

    if (!indexUniqueIdentifierToUse.value) return null;

    return {
      name: isPeerGroupPortfolio.value ? RouteName.FD_FACTOR_COMPARISON : RouteName.FD_REGRESSION_TABLE,
      params: {
        indexUniqueIdentifier: indexUniqueIdentifierToUse.value,
      },
      query: itemUnderAnalysisPortfolioTreeId.value
        ? {
            id: itemUnderAnalysisPortfolioTreeId.value,
          }
        : undefined,
    };
  });

  const performanceAttributionTo = computed(() => {
    if (!indexUniqueIdentifierToUse.value) return null;

    return {
      name: RouteName.STRATEGY_PERFORMANCE_CONTRIBUTION,
      params: {
        indexUniqueIdentifier: indexUniqueIdentifierToUse.value,
      },
    };
  });

  return {
    constituentTo,
    portfolioConstructionTo,
    factsheetTo,
    clusteringAnalysisTo,
    positionTo,
    factorDecompositionTo,
    performanceAttributionTo,
  };
}
